<template>
  <div class="build-tabs" role="tablist" aria-label="Open builds">
    <button
      v-for="build in builds"
      :key="build.archetype.id"
      type="button"
      role="tab"
      class="build-tab"
      :class="[`path-${pathSlug(build.archetype)}`, { 'is-active': activeId === build.archetype.id }]"
      :aria-selected="activeId === build.archetype.id"
      @click="$emit('select', build.archetype.id)"
    >
      <img
        v-if="build.archetype.icon"
        class="build-tab__art"
        :src="build.archetype.icon"
        alt=""
      />
      <span v-else class="build-tab__art build-tab__art--empty">{{ initials(build.archetype) }}</span>

      <span class="build-tab__text">
        <span class="build-tab__name">{{ build.archetype.name }}</span>
        <MetatypeBadge
          size="sm"
          :metatype="build.profile.verdict.metatype"
          :runner-up="build.profile.verdict.runner_up"
          :hybrid="build.profile.verdict.is_hybrid"
        />
      </span>

      <span
        class="build-tab__close"
        role="button"
        tabindex="-1"
        :aria-label="`Close ${build.archetype.name}`"
        @click.stop="$emit('close', build.archetype.id)"
      >&times;</span>
    </button>

    <!-- Comparing is what the tool is for, so the matrix is a tab of its own
         rather than a mode switch hidden somewhere else. It only exists once
         there are two builds to lay against each other. -->
    <button
      v-if="builds.length > 1"
      type="button"
      role="tab"
      class="build-tab build-tab--compare"
      :class="{ 'is-active': activeId === 'compare' }"
      :aria-selected="activeId === 'compare'"
      @click="$emit('select', 'compare')"
    >
      <span class="build-tab__glyph">⇹</span>
      <span class="build-tab__text">
        <span class="build-tab__name">Compare</span>
        <span class="build-tab__sub">{{ builds.length }} builds</span>
      </span>
    </button>

    <span class="build-tabs__hint">{{ builds.length }}/{{ maxSelection }} open</span>
  </div>
</template>

<script setup>
import MetatypeBadge from './MetatypeBadge.vue';

defineProps({
  builds: { type: Array, required: true },
  activeId: { type: String, default: null },
  maxSelection: { type: Number, default: 6 },
});

defineEmits(['select', 'close']);

function initials(archetype) {
  return archetype.name
    .split(/[\s-]+/)
    .slice(0, 2)
    .map((word) => word.charAt(0))
    .join('');
}

function pathSlug(archetype) {
  return (archetype.path ?? '').replace('Path of ', '').toLowerCase() || 'none';
}
</script>

<style scoped>
.build-tabs {
  display: flex;
  align-items: stretch;
  gap: var(--gm-gap-xs);
  padding: 0 0 var(--gm-gap-xs);
  overflow-x: auto;
  scrollbar-width: none;
}

.build-tabs::-webkit-scrollbar {
  display: none;
}

.build-tab {
  position: relative;
  display: flex;
  align-items: center;
  gap: var(--gm-gap-sm);
  flex: 0 0 auto;
  max-width: 15rem;
  padding: 0.3rem 1.5rem 0.3rem 0.3rem;
  font: inherit;
  text-align: left;
  color: inherit;
  background: var(--gm-surface-1);
  border: 1px solid var(--gm-border-dim);
  border-bottom-color: transparent;
  border-top: 2px solid var(--path-color, transparent);
  border-radius: var(--gm-radius-sm) var(--gm-radius-sm) 0 0;
  cursor: pointer;
  opacity: 0.62;
  transition: opacity 0.15s ease, background 0.15s ease;
}

.build-tab.path-json { --path-color: var(--gm-info); }
.build-tab.path-john { --path-color: var(--gm-accent); }

.build-tab:hover {
  opacity: 0.85;
}

.build-tab.is-active {
  opacity: 1;
  background: var(--gm-surface-2);
  border-color: var(--gm-border);
  border-top-color: var(--path-color, var(--gm-accent));
  border-bottom-color: transparent;
}

.build-tab--compare {
  padding-right: 0.6rem;
  --path-color: var(--gm-accent-hi);
}

.build-tab__art {
  flex: 0 0 auto;
  width: 1.8rem;
  height: 1.8rem;
  object-fit: cover;
  object-position: 50% 14%;
  border-radius: var(--gm-radius-sm);
  background: var(--gm-surface-sunken);
}

.build-tab__art--empty {
  display: grid;
  place-items: center;
  font-family: var(--gm-font-display);
  font-size: 0.62rem;
  font-weight: 700;
  color: var(--gm-text-dim);
}

.build-tab__glyph {
  display: grid;
  place-items: center;
  width: 1.8rem;
  height: 1.8rem;
  font-size: 1rem;
  color: var(--gm-accent-hi);
}

.build-tab__text {
  display: flex;
  flex-direction: column;
  gap: 0.12rem;
  min-width: 0;
}

.build-tab__name {
  font-family: var(--gm-font-display);
  font-size: 0.64rem;
  font-weight: 600;
  letter-spacing: 0.03em;
  color: var(--gm-text);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.build-tab__sub {
  font-size: 0.55rem;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--gm-text-dim);
}

.build-tab__close {
  position: absolute;
  top: 50%;
  right: 0.3rem;
  transform: translateY(-50%);
  display: grid;
  place-items: center;
  width: 1rem;
  height: 1rem;
  font-size: 0.85rem;
  line-height: 1;
  color: var(--gm-text-dim);
  border-radius: var(--gm-radius-sm);
}

.build-tab__close:hover {
  color: var(--gm-text);
  background: var(--gm-surface-sunken);
}

.build-tabs__hint {
  align-self: center;
  margin-left: auto;
  padding-left: var(--gm-gap);
  font-family: var(--gm-font-display);
  font-size: 0.55rem;
  letter-spacing: 0.09em;
  text-transform: uppercase;
  color: var(--gm-text-dim);
  white-space: nowrap;
}
</style>
